import deepEqual from 'fast-deep-equal'
import type { Duration } from 'luxon'

import type { Activity, ActivityInput } from '@orus.eu/activity'
import { type Jsonable, ServerOutdatedError, TechnicalError } from '@orus.eu/error'
import type { FieldSpecification } from '@orus.eu/message'
import { type Failure, failure, isFailure, type Result } from '@orus.eu/result'
import { type Language, languages, type ParameterlessTranslationKey, translate } from '@orus.eu/translations'
import type { PartialNullable } from '@orus.eu/utils'

import type { InitStrategy } from './dimension-init-strategy.ts'
import type { EndorsementValidationState, SubscriptionActionType } from './types.ts'

export type StateManagerWalkOperation =
  | 'applyInputDifference'
  | 'refreshAllComputedDimensions'
  | 'resetComputedDimensionsAndComputePersistableDifference'
  | 'getStateWithRetroactiveDimensionsUpdated'

export type StateManagerWalkOutcome = 'success' | 'validation-failure' | 'error'

export type StateManagerWalkReport = {
  totalDurationMs: number
  syncDurationMs: number
  asyncDurationMs: number
  dimensionsVisited: number
  dimensionUpdatesAttempted: number
  functionsExecuted: number
  outcome: StateManagerWalkOutcome
}

export type StateManagerWalkObserver = {
  finish: (report: StateManagerWalkReport) => void
}

export type DimensionFunctionExecutionOutcome = 'executed' | 'skipped-context' | 'skipped-missing-dependency'

export type DimensionFunctionExecutionAlertHandler<Context> = {
  /**
   * Maximum reasonable event loop blocking time for a high level state manager operation.
   * This doesn't include waiting for asynchronous dimensions to resolve.
   */
  globalSyncTimeAlertThresholdMs: number
  /**
   * Called when the total time spend in sync computations during a state manager operation
   * is greater than the threshold. The total time is the duration of the operation minus the time
   * spent waiting for asynchronous dimensions to resolve.
   */
  handleGlobalSyncTimeAlert: (
    context: Context,
    operation: StateManagerWalkOperation,
    totalSyncExecutionTimeMs: number,
  ) => void
  /**
   * Maximum reasonable event loop blocking time to compute a single dimension.
   * This doesn't include waiting for asynchronous dimensions to resolve.
   */
  dimensionSyncTimeAlertThresholdMs: number
  /**
   * Called when the time spend in sync computations to compute a single dimension
   * is greater than the threshold. The time is the duration before the dimension returns it's
   * result, or it's promise for async dimensions.
   */
  handleDimensionSyncTimeAlert: (context: Context, dimension: AbstractDimension, syncExecutionTimeMs: number) => void
  startWalk?: (context: Context, operation: StateManagerWalkOperation) => StateManagerWalkObserver
}

/**
 * A dimension specifies the type and features of a field useful for a policy
 */
export abstract class AbstractDimension<NAME extends string = string, TYPE = unknown> {
  public readonly options: DimensionOptions<NAME, TYPE>

  readonly name: NAME
  readonly displayNames: Record<Language, string>
  readonly maxLength?: number | undefined
  readonly placeholders?: Record<Language, string> | undefined
  readonly ariaLabels?: Record<Language, string> | undefined
  readonly hints?: Record<Language, string> | undefined
  readonly computedAfterTimestamp?: number | undefined
  readonly initStrategy?: InitStrategy<Record<string, unknown>, readonly string[], TYPE, never> | undefined

  constructor(options: DimensionOptions<NAME, TYPE>) {
    this.options = options

    if (!/^[a-zA-Z\d]+$/.test(options.name)) {
      throw new TechnicalError(
        'Invalid dimension name : For all features to work properly, dimension names must not be empty and must contain only ascii letters or numbers',
        { context: { offendingDimensionName: options.name } },
      )
    }
    this.name = options.name
    this.initStrategy = options.initStrategy

    if (this.initStrategy?.computeValue && this.initStrategy.oldNames.length === 0) {
      throw new TechnicalError('Invalid initStrategy : computeValue requires at least one oldName', {
        context: { dimensionName: options.name },
      })
    }

    this.displayNames = getParmeterlessKeyTranslations(options.displayKeys.name)

    this.placeholders = options.displayKeys.placeholder
      ? getParmeterlessKeyTranslations(options.displayKeys.placeholder)
      : undefined

    this.ariaLabels = options.displayKeys.ariaLabel
      ? getParmeterlessKeyTranslations(options.displayKeys.ariaLabel)
      : undefined

    this.hints = options.displayKeys.hint ? getParmeterlessKeyTranslations(options.displayKeys.hint) : undefined

    this.maxLength = options.maxLength ?? undefined

    this.computedAfterTimestamp = options.computedAfterTimestamp
  }

  get piiCategory(): DimensionPiiCategory | undefined {
    return this.options.pii
  }

  readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter | undefined = undefined

  mcpAdapter: DimensionMcpAdapter<TYPE> | undefined = undefined

  /** Throws ServerOutdatedError when the state holds a value this release cannot validate, as a newer release may write */
  throwServerOutdatedIfValueInvalid(state: LooselyTypedData): void {
    const value = state[this.name]
    if (value != undefined && isFailure(this.validateData(value))) {
      throw new ServerOutdatedError(
        'While computing the subscription state, encountered a dimension value this release cannot validate, written by a newer release',
        {
          context: { dimension: this.name, value: String(value), acceptedValues: this.acceptedValues.map(String) },
        },
      )
    }
  }

  /** The values this release accepts, for a dimension limited to a list of them. */
  get acceptedValues(): readonly TYPE[] {
    throw new TechnicalError(
      'Unsupported operation : this dimension does not have a list of accepted values. Override this getter before using it.',
      { context: { name: this.name } },
    )
  }

  validateData(_value: LooselyTypedValue): Result<TYPE, DimensionValidationProblem> {
    throw new TechnicalError(
      'Unsupported operation : this dimension is meant to be only used as a computed dimension. Override this method before using the dimension as an input dimension.',
      { context: { name: this.name } },
    )
  }

  readonly valueStringFormatter: ValueStringFormatter<TYPE> | undefined = undefined

  abstract formatToString(value: TYPE, opts: { language: Language }): string | undefined

  /**
   * Directly sets the fields value in a state compatible with this dimension
   * @param state
   * @param value
   */
  setFieldValue(state: { [name in NAME]?: TYPE }, value: TYPE | null): void {
    if (value === null) {
      delete state[this.name]
      return
    }

    state[this.name] = value
  }

  /**
   * Sets the value of the field in a partial state that represents an update, allowing
   * null value to represent the removal of the field.
   */
  setUpdateFieldValue(update: { [name in NAME]?: TYPE | null }, value: TYPE | null): void {
    update[this.name] = value
  }
  /**
   * Return the field value corresponding to the dimension in a partial state
   * @param state
   * @param value
   */
  getPartialStateFieldValue(state: { [name in NAME]?: TYPE | null }): TYPE | undefined {
    return state[this.name] ?? undefined
  }

  isFieldMissingInPartialState(state: { [name in NAME]?: TYPE | null }): boolean {
    return this.getPartialStateFieldValue(state) == undefined
  }

  mergePartialStates(a: { [name in NAME]?: TYPE | null }, b: { [name in NAME]?: TYPE | null }): void {
    if (b[this.name] != undefined) a[this.name] = b[this.name]
  }

  mergePartialUpdates(a: { [name in NAME]?: TYPE | null }, b: { [name in NAME]?: TYPE | null }): void {
    if (b[this.name] === undefined) return
    else if (b[this.name] === null) a[this.name] = null
    else a[this.name] = b[this.name]
  }

  /**
   * Return the field value from a state object
   * @param state
   * @param value
   */
  getFieldValue(state: { [name in NAME]: TYPE }): TYPE {
    return state[this.name]
  }

  copyField(input: { [key in NAME]?: TYPE }, output: { [key in NAME]?: TYPE }): void {
    output[this.name] = input[this.name]
  }

  updateField(state: { [key in NAME]?: TYPE }, update: { [key in NAME]?: TYPE | null }): void {
    const newValue = update[this.name]

    if (newValue === undefined) {
      // dimension irrelevant for this update
      return
    }

    if (newValue === null) {
      // null in updates means that we want to delete the field
      delete state[this.name]
      return
    }

    state[this.name] = newValue
  }

  shouldRecomputeDimensionBasedOnContext<
    Context extends { subscriptionActionType: SubscriptionActionType },
    Dependencies extends readonly AbstractDimension[],
  >(
    context: Context,
    dimensionApplyContext: DimensionApplyContext | undefined,
    state?: PartialDimensionnedState<Dependencies>,
  ): boolean {
    if (!dimensionApplyContext) {
      throw new TechnicalError('Dimension apply context is undefined', {
        context: {
          dimensionName: this.name,
          dimensionApplyContext,
          context,
        },
      })
    }

    const subscriptionActionType = context['subscriptionActionType']

    switch (subscriptionActionType) {
      case 'quote':
        return dimensionApplyContext.quote(state)
      case 'renewal':
        return dimensionApplyContext.renewal(state)
      case 'endorsement':
        return dimensionApplyContext.endorsement(state)
      case 'display':
        return dimensionApplyContext.display?.(state) ?? true
    }
  }

  async updatePartialStateFieldWithComputedDimensionField<
    Dependencies extends readonly AbstractDimension[],
    Context extends { subscriptionActionType: SubscriptionActionType },
  >(
    state: PartialDimensionnedState<[...Dependencies, this]>,
    dimensionFunctions: {
      [key in NAME]?: (state: DimensionnedState<Dependencies>, context: Context) => TYPE | Promise<TYPE>
    },
    partialDimensionFunctions: {
      [key in NAME]?: (state: PartialDimensionnedState<Dependencies>, context: Context) => TYPE | Promise<TYPE>
    },
    dimensionApplyContext: DimensionApplyContext<Dependencies> | undefined,
    dependencyMatrix: Record<NAME, Dependencies>,
    /** The dependencies flagged newerReleaseMayAddValues, precomputed by the state manager */
    newerReleaseMayAddValuesDependencies: readonly AbstractDimension[],
    context: Context,
    alertHandler: DimensionFunctionExecutionAlertHandler<Context>,
  ): Promise<{
    /**
     * The total time spent in async computations, necessary to compute the state
     * manager performance metrics
     */
    asyncExecutionTimeMs: number
    executionOutcome: DimensionFunctionExecutionOutcome
  }> {
    const dimensionFunction = dimensionFunctions[this.name]

    if (dimensionFunction) {
      if (!this.shouldRecomputeDimensionBasedOnContext(context, dimensionApplyContext, state)) {
        // The dimension should not be recomputed in the current context, so we skip it
        return { asyncExecutionTimeMs: 0, executionOutcome: 'skipped-context' }
      }

      for (const dependency of newerReleaseMayAddValuesDependencies) dependency.throwServerOutdatedIfValueInvalid(state)

      const dependencies = dependencyMatrix[this.name]
      for (const dependency of dependencies) {
        const value = dependency.getPartialStateFieldValue(state)
        if (value == undefined) {
          // We don't attempt computing the dimension because one dependency is missing or null.
          // and we need to erase it if it was already set in the updated state
          const stateToUpdate = state as { [key in NAME]?: TYPE }
          if (value == undefined && this.getPartialStateFieldValue(stateToUpdate) != undefined) {
            this.setFieldValue(stateToUpdate, null)
          }
          return { asyncExecutionTimeMs: 0, executionOutcome: 'skipped-missing-dependency' }
        }
      }

      const start = performance.now()
      const functionOutput = dimensionFunction(state as unknown as DimensionnedState<Dependencies>, context)
      const syncExecutionTimeMs = performance.now() - start

      if (syncExecutionTimeMs > alertHandler.dimensionSyncTimeAlertThresholdMs) {
        alertHandler.handleDimensionSyncTimeAlert(context, this, syncExecutionTimeMs)
      }

      const startAsync = performance.now()
      const functionResolution = await functionOutput
      const asyncExecutionTimeMs = performance.now() - startAsync
      const value = functionResolution ?? null

      this.setFieldValue(state as { [key in NAME]?: TYPE }, value)

      return { asyncExecutionTimeMs, executionOutcome: 'executed' }
    }

    const partialDimensionFunction = partialDimensionFunctions[this.name]

    if (!partialDimensionFunction) {
      throw new TechnicalError('Dimension function neither in dimensionFunctions nor in partialDimensionFunctions', {
        context: {
          dimensionName: this.name,
          dimensionFunctions: Object.keys(dimensionFunctions),
          partialDimensionFunctions: Object.keys(partialDimensionFunctions),
        },
      })
    }

    if (!this.shouldRecomputeDimensionBasedOnContext(context, dimensionApplyContext, state)) {
      // The dimension should not be recomputed in the current context, so we skip it
      return { asyncExecutionTimeMs: 0, executionOutcome: 'skipped-context' }
    }

    for (const dependency of newerReleaseMayAddValuesDependencies) dependency.throwServerOutdatedIfValueInvalid(state)

    const start = performance.now()
    const functionOutput = partialDimensionFunction(state as PartialDimensionnedState<Dependencies>, context)
    const syncExecutionTimeMs = performance.now() - start

    if (syncExecutionTimeMs > alertHandler.dimensionSyncTimeAlertThresholdMs) {
      alertHandler.handleDimensionSyncTimeAlert(context, this, syncExecutionTimeMs)
    }

    const startAsync = performance.now()
    const value = await functionOutput
    const asyncExecutionTimeMs = performance.now() - startAsync

    this.setFieldValue(state as { [key in NAME]?: TYPE }, value)

    return { asyncExecutionTimeMs, executionOutcome: 'executed' }
  }

  areValuesEquivalent(a: TYPE | null | undefined, b: TYPE | null | undefined): boolean {
    return deepEqualAssimilateNullUndefined(a, b)
  }

  updateDifference(
    before: { [key in NAME]?: TYPE | null },
    after: { [key in NAME]?: TYPE | null },
    difference: { [key in NAME]?: TYPE | null },
  ): void {
    const valueBefore = before[this.name]
    const valueAfter = after[this.name]

    if (valueBefore == undefined) {
      if (valueAfter == undefined) {
        // nothing changed, field must be absent from difference
        delete difference[this.name]
        return
      }

      // field was absent but now exits, must create
      difference[this.name] = valueAfter
      return
    }

    if (valueAfter == undefined) {
      // field existed but not anymore, must set to null to delete the field
      difference[this.name] = null
      return
    }

    if (this.areValuesEquivalent(valueBefore, valueAfter)) {
      // nothing changed, field must be absent from difference
      delete difference[this.name]
      return
    }

    // field was updated, must set to after
    difference[this.name] = valueAfter
  }
}

export type DimensionTrackingMessageFieldAdapter<T = unknown> = {
  getTrackingFieldSpecification(): FieldSpecification

  convertStateValueToTrackingValue(stateValue: T | null | undefined): number | string | string[] | boolean | null
}

export type DimensionMcpAdapter<T = unknown> = {
  /**
   * Attempts to parse the value from the MCP value input, and provides a precise explanation in human readable language of why the parsing failed.
   * This explanation will be read by the LLM to understand what went wrong.
   */
  parseValueFromMcp(mcpValueInput: string): Result<T, string>

  /**
   * A human readable explanation of how to format the value for the MCP.
   * This explanation will be read by the LLM to understand what to do with this field
   */
  formattingInstructions: string
}

export type ValueStringFormatter<T = unknown> = {
  formatToString(value: T, opts: { language: Language }): string | undefined
}

export function deepEqualAssimilateNullUndefined(a: unknown, b: unknown): boolean {
  if (deepEqual(a, b)) return true
  if (a == undefined) {
    return b == undefined
  }
  if (b == undefined) return false
  if (typeof a === 'object') {
    if (typeof b !== 'object') return false
    for (const aKey in a) {
      const aValue = (a as Record<string, unknown>)[aKey]
      const bValue = aKey in b ? (b as Record<string, unknown>)[aKey] : undefined
      if (!deepEqualAssimilateNullUndefined(aValue, bValue)) return false
    }
    for (const bKey in b) {
      if (!(bKey in a)) {
        const bValue = bKey in b ? (b as Record<string, unknown>)[bKey] : undefined
        if (bValue != undefined) return false
      }
    }
    return true
  }
  return false
}

export type DimensionDisplayTextsSpec = {
  /**
   * Specifies translation keys for the display texts
   */
  displayKeys: {
    /**
     * Translation key for the name as displayed in the UI and exports, seen by the end users
     */
    name: ParameterlessTranslationKey
    /**
     * Translation key for an example value that can be used as a placeholder in the UI
     */
    placeholder?: ParameterlessTranslationKey
    /**
     * Translation key for the aria label for the field in the UI
     */
    ariaLabel?: ParameterlessTranslationKey
    /**
     * Translation key for a hint displayed in the UI to explaine why this dimension is about
     */
    hint?: ParameterlessTranslationKey
  }
}

export type InputLockingSpecs = {
  lockedInQuoteFor: Partial<Record<'client' | 'partner' | 'platform', StateManagerCondition>>
  lockedInEndorsementFor?: Partial<Record<'client' | 'partner' | 'platform', StateManagerCondition>>
  lockedInRenewalFor?: Partial<Record<'client' | 'partner' | 'platform', StateManagerCondition>>
}

/**
 * Category of sensitive PII carried by a dimension; rendered as `data-pii` on
 * the DOM so session-replay tools blackbox the subtree. Currently:
 *
 * - `'health'` — GDPR Article 9 data (medical questionnaires, smoker status,
 *   dangerous-sports practice, BMI…). Labels and values are equally sensitive.
 *
 * See `docs/session-replay-privacy.md` for the end-to-end mechanism and the
 * rules for adding new categories.
 */
export type DimensionPiiCategory = 'health'

export type DimensionOptions<NAME, TYPE = unknown> = {
  /**
   * Name of the field in state models
   */
  name: NAME
  /**
   * Backward compatibility initialization for dimensions that have been renamed or computed from old names.
   * This is used to support legacy state models without migrations.
   *
   * A strategy that reads the dimension's previous value narrows it by annotating its own
   * `previousValue` parameter, the way it already annotates `state`.
   */
  initStrategy?: InitStrategy<Record<string, unknown>, readonly string[], TYPE, never>

  /**
   * Maximum length of the dimension value
   */
  maxLength?: number | undefined

  /**
   * Disable dimension when the subscription is locked
   */
  inputLocking: InputLockingSpecs | undefined

  /**
   * When true, allows editing the dimension even when a user exists, but only if no initial value was defined.
   * This is useful for fields like phone that should remain editable for new subscriptions even after user creation.
   * When false or undefined (default), the dimension is disabled whenever a user exists (if in the disabledFieldsWhenCustomerId list).
   */
  allowEditionWhenUserExistsIfNoInitialValue?: boolean

  /**
   * Skips computed dimension recomputation for state versions older than this timestamp.
   *
   * Read path only: `getStateWithRetroactiveDimensionsUpdated` compares this against the state version
   * timestamp its caller passes, which can be diverse. `applyInputDifference` and `refreshAllComputedDimensions` ignore it entirely.
   *
   * So this is not a rollout gate, and a version whose payload was frozen before being materialized has no
   * timestamp that means "when these values were computed". Anything a stored dimension depends on must be
   * recorded when it is computed — an input written by a trigger — rather than gated here. The guard in
   * state-manager-factory rejects the combination.
   */
  computedAfterTimestamp?: number | undefined

  /**
   * A newer release may add values to this dimension. A computed dimension depending on a value this release cannot
   * validate throws ServerOutdatedError instead of reading it: a pod left behind by a rolling deploy (INS-2171).
   */
  newerReleaseMayAddValues?: true

  /**
   * Whether this dimension should be included in tracking messages.
   * Historically, all dimensions were included in tracking messages, but we encountered issues with the size of the payload in Segment.
   * Therefore, we now whitelist dimensions that should be included in tracking messages.
   */
  includeInTrackingMessages: boolean

  /**
   * Marks the dimension as carrying sensitive PII of the given category. The
   * matching subscription-UI renderer wraps the input with `data-pii=<category>`,
   * which makes PostHog session replay and Sentry Replay blackbox the subtree.
   *
   * Currently honoured by `BooleanSubscriptionUiElementBlock` and
   * `SelectMultipleSubscriptionUiElementBlock` (the latter also forwards to the
   * pharaoh `Select`'s `pii` prop to cover the portaled MUI menu).
   *
   * Orthogonal to `includeInTrackingMessages` above — this only controls
   * session-replay visibility, not whether the value is sent as an event
   * property.
   *
   * See `docs/session-replay-privacy.md` for the full mechanism and how to
   * extend it.
   */
  pii?: DimensionPiiCategory

  /**
   * Whether this dimension should be considered for difference computation in the UI.
   * This is useful for dimensions that are not relevant for the user to see the difference, such as the product code.
   *
   * Note that this might differ for clients, partners and platforms.
   */
  considerForDifferenceComputationInUi: {
    client: boolean
    partner: boolean
    platform: boolean
  }
} & DimensionDisplayTextsSpec

export type TypeOfDimension<D extends AbstractDimension> =
  D extends AbstractDimension<string, infer TYPE> ? TYPE : never

export type DimensionFunction<
  D extends AbstractDimension,
  InputDimensions extends readonly AbstractDimension[],
  Context,
> =
  D extends AbstractDimension<string, infer TYPE>
    ? (inputState: DimensionnedState<InputDimensions>, context: Context) => TYPE | undefined | Promise<TYPE | undefined>
    : never
export type SyncDimensionFunction<
  D extends AbstractDimension,
  InputDimensions extends readonly AbstractDimension[],
  Context,
> =
  D extends AbstractDimension<string, infer TYPE>
    ? (inputState: DimensionnedState<InputDimensions>, context: Context) => TYPE | undefined
    : never
export type SafeAsyncDimensionFunction<
  OUTPUT,
  PROBLEM,
  D extends AbstractDimension<string, Result<OUTPUT, PROBLEM>>,
  InputDimensions extends readonly AbstractDimension[],
  Context,
> =
  D extends AbstractDimension<string, infer TYPE>
    ? (inputState: DimensionnedState<InputDimensions>, context: Context) => Promise<TYPE | undefined>
    : never
export type AsyncDimensionFunction<
  OUTPUT,
  D extends AbstractDimension<string, OUTPUT>,
  InputDimensions extends readonly AbstractDimension[],
  Context,
> =
  D extends AbstractDimension<string, infer TYPE>
    ? (inputState: DimensionnedState<InputDimensions>, context: Context) => Promise<TYPE | undefined>
    : never

export type ConditionFunction<Dimensions extends readonly AbstractDimension[]> = (
  inputState: PartialDimensionnedState<Dimensions>,
) => boolean

export type PartialDimensionFunction<
  D extends AbstractDimension,
  InputDimensions extends readonly AbstractDimension[],
  Context,
> =
  D extends AbstractDimension<string, infer TYPE>
    ? (
        inputState: PartialDimensionnedState<InputDimensions>,
        context: Context,
      ) => TYPE | undefined | Promise<TYPE | undefined>
    : never

export type SyncPartialDimensionFunction<
  D extends AbstractDimension,
  InputDimensions extends readonly AbstractDimension[],
  Context,
> =
  D extends AbstractDimension<string, infer TYPE>
    ? (inputState: PartialDimensionnedState<InputDimensions>, context: Context) => TYPE | undefined
    : never
export type AsyncPartialDimensionFunction<
  OUTPUT,
  D extends AbstractDimension<string, OUTPUT>,
  InputDimensions extends readonly AbstractDimension[],
  Context,
> =
  D extends AbstractDimension<string, infer TYPE>
    ? (inputState: PartialDimensionnedState<InputDimensions>, context: Context) => Promise<TYPE | undefined>
    : never
export type SafeAsyncPartialDimensionFunction<
  OUTPUT,
  PROBLEM,
  D extends AbstractDimension<string, Result<OUTPUT, PROBLEM>>,
  InputDimensions extends readonly AbstractDimension[],
  Context,
> =
  D extends AbstractDimension<string, infer TYPE>
    ? (inputState: PartialDimensionnedState<InputDimensions>, context: Context) => Promise<TYPE | undefined>
    : never
export type DimensionnedState<Dimensions extends readonly AbstractDimension[]> = {
  [D in Dimensions[number] as D['name']]: TypeOfDimension<D>
}

/**
 * Describes an dimensionned state where all the dimensions are not known.
 * null, undefined, or missing indicate that a field is not known
 * This object is also used to reprensent an update to the state. In this case, null means that the field should be reset
 */
export type PartialDimensionnedState<Dimensions extends readonly AbstractDimension[]> = PartialNullable<
  DimensionnedState<Dimensions>
>

// Module-augmentation registry (mirrors TanStack Router's `Register`). A consumer augments this with
// `{ state: <FlatStateInterface>; context: <ItsContext> }`; the resolvers below then return that flat state
// type for managers whose Context matches — and fall back to the inferred generic mapped type otherwise,
// so every other (test / future) state manager is completely unaffected.
// oxlint-disable-next-line typescript/consistent-type-definitions, @typescript-eslint/no-empty-object-type -- interface required for declaration merging into this registry; empty by design (consumers augment it)
export interface DimensionsRegister {}

export type RegisteredDimensionnedState<
  Dimensions extends readonly AbstractDimension[],
  Context,
> = DimensionsRegister extends { state: infer S; context: infer C }
  ? [Context] extends [C]
    ? S
    : DimensionnedState<Dimensions>
  : DimensionnedState<Dimensions>

export type RegisteredPartialDimensionnedState<
  Dimensions extends readonly AbstractDimension[],
  Context,
> = DimensionsRegister extends { state: infer S; context: infer C }
  ? [Context] extends [C]
    ? PartialNullable<S>
    : PartialDimensionnedState<Dimensions>
  : PartialDimensionnedState<Dimensions>

/**
 * Same as {@link RegisteredPartialDimensionnedState}, but for helpers that are not parameterized by a
 * Context (e.g. generic free functions). Resolves to the registered flat state whenever one is registered,
 * regardless of which Context it was registered for, and falls back to the generic mapped type otherwise.
 */
export type RegisteredPartialDimensionnedStateAnyContext<Dimensions extends readonly AbstractDimension[]> =
  DimensionsRegister extends { state: infer S } ? PartialNullable<S> : PartialDimensionnedState<Dimensions>

/**
 * Loosely typed data structure that matches any potential dimension data
 */
export type LooselyTypedData = { [key: string]: unknown }
/**
 * Losely typed data structure that matches any potential dimension value
 */
export type LooselyTypedValue = unknown

/**
 * Applies our conventions for document templates tags: replaces camel case with uppercase + snake case
 * @param name
 */
export function toDocumentTemplateTag(name: string): string {
  return name
    .split(/(?=[A-Z])/)
    .join('_')
    .toUpperCase()
}

export type DimensionValidationProblem = {
  type: 'dimension-validation-problem'
  /**
   * Human - readable reason for the validation problem, suitable for a frontend developer
   * (end users should not see this because of surface validation)
   */
  explanation: string
}

export function dimensionValidationFailure(explanation: string): Failure<DimensionValidationProblem> {
  return failure({
    type: 'dimension-validation-problem',
    explanation,
  })
}

export type CacheParams<
  OUTPUT,
  PROBLEM,
  Dimensions extends readonly AbstractDimension[],
  INPUT extends DimensionnedState<Dimensions>,
  Context,
> = {
  version: number
  name: string
  dimensions: Dimensions
  callback: (input: INPUT, context: Context) => Promise<Result<OUTPUT, PROBLEM> | undefined>
  ttl: Duration | undefined
}

export type CacheService = {
  cache<
    OUTPUT,
    PROBLEM,
    Dimensions extends readonly AbstractDimension[],
    INPUT extends DimensionnedState<Dimensions>,
    Context,
  >(
    params: CacheParams<OUTPUT, PROBLEM, Dimensions, INPUT, Context>,
  ): (input: INPUT, context: Context) => Promise<Result<OUTPUT, PROBLEM> | undefined>
  loggingWorker: {
    name: string
    start(): void
    stop(): Promise<void>
    isRunning(): boolean
  }
}

export class MockCacheService implements CacheService {
  cache<
    OUTPUT,
    PROBLEM,
    Dimensions extends readonly AbstractDimension[],
    INPUT extends DimensionnedState<Dimensions>,
    Context,
  >({
    callback,
  }: CacheParams<OUTPUT, PROBLEM, Dimensions, INPUT, Context>): (
    input: INPUT,
    context: Context,
  ) => Promise<Result<OUTPUT, PROBLEM> | undefined> {
    return callback
  }
  loggingWorker = {
    name: 'mock-cache-service-logging-worker',
    start: (): void => {},
    stop: (): Promise<void> => Promise.resolve(),
    isRunning: (): boolean => false,
  }
}

export const mockDimensionFunctionExecutionAlertHandler: DimensionFunctionExecutionAlertHandler<unknown> = {
  dimensionSyncTimeAlertThresholdMs: 1_000, // arbitrary value
  handleDimensionSyncTimeAlert: () => {},
  globalSyncTimeAlertThresholdMs: 1_000, // arbitrary value
  handleGlobalSyncTimeAlert: () => {},
}

export function checkDimensionsDefined<Dimensions extends readonly AbstractDimension[]>(
  state: PartialDimensionnedState<Dimensions>,
  dimensions: Dimensions,
  assertionContext: Jsonable,
): DimensionnedState<Dimensions> {
  for (const dimension of dimensions) {
    if (dimension.isFieldMissingInPartialState(state)) {
      throw new TechnicalError('Missing field in partial state', {
        context: { assertionContext, missingField: dimension.name },
      })
    }
  }
  return state as unknown as DimensionnedState<Dimensions>
}

export const LOCALE = 'fr'
export type GramaticalGender = 'feminine' | 'masculine'
export type Capitalization = 'capitalized' | 'normal'

export class BaseDimension<NAME extends string = string, TYPE = unknown> extends AbstractDimension<NAME, TYPE> {
  constructor(options: DimensionOptions<NAME, TYPE> & { stringFormatter: (value: TYPE) => string | undefined }) {
    super(options)
    this.stringFormatter = options.stringFormatter
  }

  override formatToString(value: TYPE): string | undefined {
    if (this.stringFormatter) {
      return this.stringFormatter(value)
    }
    return undefined
  }

  stringFormatter: ((value: TYPE) => string | undefined) | undefined
}

export function isDimensionnedStateUpdateEmpty(
  state: RegisteredPartialDimensionnedStateAnyContext<readonly AbstractDimension[]>,
): boolean {
  // undefined values are accepted because they don't mean anything specific
  // null values are not accepted because they mean resetting the field
  return Object.values(state).every((value) => value === undefined)
}

export function getParmeterlessKeyTranslations(key: ParameterlessTranslationKey): Record<Language, string> {
  return Object.fromEntries(languages.map((language) => [language, translate(key, language)])) as Record<
    Language,
    string
  >
}

export type StateManagerCondition<NAME extends string = string, T = unknown, PROBLEM = unknown> =
  | AbstractDimension<NAME, boolean>
  | boolean
  | {
      readonly or: readonly StateManagerCondition[]
    }
  | {
      readonly and: readonly StateManagerCondition[]
    }
  | {
      readonly not: StateManagerCondition
    }
  | {
      readonly defined: AbstractDimension
    }
  | {
      readonly notEmpty: AbstractDimension<NAME, ReadonlyArray<T>>
    }
  | {
      readonly empty: AbstractDimension<NAME, ReadonlyArray<T>>
    }
  | {
      readonly isFailure: AbstractDimension<NAME, Result<T, PROBLEM>>
    }
  | {
      readonly isSuccess: AbstractDimension<NAME, Result<T, PROBLEM>>
    }
  | {
      readonly equal: {
        readonly dimension: AbstractDimension<NAME, T>
        readonly value: T
      }
    }
  | {
      readonly notEqual: {
        readonly dimension: AbstractDimension<NAME, T>
        readonly value: unknown
      }
    }
  | {
      readonly activityIn: {
        readonly dimension: AbstractDimension<NAME, ActivityInput>
        readonly activities: readonly Activity[]
      }
    }
  | {
      readonly subscriptionActionType: SubscriptionActionType
    }
  | {
      readonly endorsementValidationState: EndorsementValidationState
    }
  | {
      readonly frPrelDeathBenefitClaimInProgress: boolean
    }

type ApplyContextPredicate<Dependencies extends readonly AbstractDimension[]> = (
  state?: PartialDimensionnedState<Dependencies>,
) => boolean

export type DimensionApplyContext<Dependencies extends readonly AbstractDimension[] = readonly AbstractDimension[]> = {
  quote: ApplyContextPredicate<Dependencies>
  renewal: ApplyContextPredicate<Dependencies>
  endorsement: ApplyContextPredicate<Dependencies>
  display?: ApplyContextPredicate<Dependencies>
}
